import React, { createContext, useContext, useMemo } from 'react';

const DashboardChartSyncContext = createContext(null);

/**
 * Snap a follower chart to the nearest timestamp tick from the hovered chart.
 * Returns -1 when no nearby point exists so the follower does not show a misleading value.
 */
export function nearestTimestampSyncMethod(ticks, data) {
  const hovered = Number(data?.activeLabel);
  if (!Number.isFinite(hovered) || !Array.isArray(ticks) || ticks.length === 0) {
    return -1;
  }

  let bestIndex = -1;
  let bestDistance = Infinity;
  const values = [];

  ticks.forEach((tick, index) => {
    const value = Number(tick?.value);
    if (!Number.isFinite(value)) {
      return;
    }
    values.push(value);
    const distance = Math.abs(value - hovered);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });

  if (bestIndex < 0) {
    return -1;
  }

  values.sort((a, b) => a - b);
  let medianGap = 60_000;
  if (values.length >= 2) {
    const gaps = [];
    for (let i = 1; i < values.length; i += 1) {
      gaps.push(values[i] - values[i - 1]);
    }
    gaps.sort((a, b) => a - b);
    medianGap = gaps[Math.floor(gaps.length / 2)] || medianGap;
  }

  const maxDistance = Math.max(medianGap * 0.75, 1000);
  return bestDistance <= maxDistance ? bestIndex : -1;
}

export function chartDataHasTimestamps(chartData) {
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return false;
  }
  return chartData.some((point) => Number.isFinite(Number(point?.timestamp)));
}

export function buildTimestampTickFormatter(chartData) {
  const labels = new Map();
  (chartData || []).forEach((point) => {
    const ts = Number(point?.timestamp);
    if (!Number.isFinite(ts)) {
      return;
    }
    const label = point.timeLabel || point.time || point.name;
    if (label != null && label !== '') {
      labels.set(ts, String(label));
    }
  });
  return (value) => {
    const ts = Number(value);
    if (labels.has(ts)) {
      return labels.get(ts);
    }
    if (Number.isFinite(ts)) {
      try {
        return new Date(ts).toLocaleString();
      } catch {
        return String(value ?? '');
      }
    }
    return String(value ?? '');
  };
}

export function buildTimestampTooltipLabelFormatter(chartData) {
  const tickFormatter = buildTimestampTickFormatter(chartData);
  return (label) => tickFormatter(label);
}

/**
 * Provides a dashboard-scoped Recharts syncId so charts on this board share a crosshair.
 * Different dashboards (and remounts) get independent sync groups.
 */
export function DashboardChartSyncProvider({ children, dashboardKey }) {
  const value = useMemo(() => {
    const key = dashboardKey == null || dashboardKey === '' ? 'dashboard' : String(dashboardKey);
    return {
      syncId: `commray-dashboard-chart-sync:${key}`,
      syncMethod: nearestTimestampSyncMethod
    };
  }, [dashboardKey]);

  return (
    <DashboardChartSyncContext.Provider value={value}>
      {children}
    </DashboardChartSyncContext.Provider>
  );
}

export function useDashboardChartSync() {
  return useContext(DashboardChartSyncContext);
}

export default DashboardChartSyncContext;
